import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft, Package, Loader2 } from 'lucide-react';

const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export default function AlunoCarrinhoPage() {
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sm_cart') || '[]'); } catch { return []; }
  });
  const [obs, setObs] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { localStorage.setItem('sm_cart', JSON.stringify(cart)); }, [cart]);

  const updateQty = (produtoId, delta) => {
    setCart(prev => prev.map(i => {
      if (i.produto_id === produtoId) {
        const limite = Number(i.estoque_disponivel || 0);
        const newQty = Math.max(
          1,
          Math.min(limite || 1, i.quantidade + delta)
        );
        return { ...i, quantidade: newQty };
      }
      return i;
    }));
  };

  const removeItem = (produtoId) => {
    setCart(prev => prev.filter(i => i.produto_id !== produtoId));
  };

  const total = cart.reduce((s, i) => s + i.preco * i.quantidade, 0);

  const finalizarPedido = async () => {
    if (cart.length === 0) { toast.error('Carrinho vazio'); return; }
    setCreating(true);
    try {
      const payload = {
        itens: cart.map(i => ({ produto_id: i.produto_id, quantidade: i.quantidade })),
        observacoes: obs
      };
      const r = await api.post('/aluno/loja/pedidos', payload);
      toast.success('Pedido criado com sucesso!');
      setCart([]);
      localStorage.removeItem('sm_cart');
      navigate(`/aluno/pedidos`);
    } catch (err) { toast.error(formatApiError(err)); }
    setCreating(false);
  };

  return (
    <div className="space-y-6 animate-fadeIn max-w-2xl" data-testid="aluno-carrinho-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/aluno/loja')} className="text-zinc-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">
          <ShoppingCart className="w-7 h-7 inline mr-2" />CARRINHO
        </h1>
      </div>

      {cart.length === 0 ? (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-12 text-center">
            <ShoppingCart className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500 mb-4">Seu carrinho esta vazio.</p>
            <Button onClick={() => navigate('/aluno/loja')} className="bg-[var(--cor-primaria,#007AFF)] hover:opacity-90" data-testid="empty-cart-shop-btn">
              Ver Produtos
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {cart.map(item => (
              <Card key={item.produto_id} className="bg-[#141414] border-[#27272A]" data-testid={`cart-item-${item.produto_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-lg bg-[#0A0A0A] flex items-center justify-center shrink-0 overflow-hidden">
                      {item.imagem_url ? (
                        <img src={item.imagem_url} alt={item.nome} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <Package className="w-6 h-6 text-zinc-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{item.nome}</p>
                      <p className="text-xs text-zinc-500">{fmt(item.preco)} / un</p>
                      {item.prazo_entrega_texto && (
                        <p className="text-xs text-blue-400 mt-1">
                          Prazo: {item.prazo_entrega_texto}
                        </p>
                      )}
                      <p className="text-[10px] text-zinc-600 mt-1">
                        Estoque disponível: {item.estoque_disponivel || 0}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="icon" variant="outline" className="h-8 w-8 border-[#27272A]" onClick={() => updateQty(item.produto_id, -1)} data-testid={`qty-minus-${item.produto_id}`}>
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="text-white font-bold w-8 text-center" data-testid={`qty-val-${item.produto_id}`}>{item.quantidade}</span>
                      <Button size="icon" variant="outline" className="h-8 w-8 border-[#27272A]" onClick={() => updateQty(item.produto_id, 1)} data-testid={`qty-plus-${item.produto_id}`}>
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold font-['Barlow_Condensed'] text-white">{fmt(item.preco * item.quantidade)}</p>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => removeItem(item.produto_id)} className="text-zinc-400 hover:text-red-400 shrink-0" data-testid={`remove-cart-${item.produto_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Observacoes (opcional)</Label>
            <Textarea value={obs} onChange={e => setObs(e.target.value)} className="bg-[#0A0A0A] border-[#27272A] mt-1 min-h-[60px]" placeholder="Alguma observacao para o pedido..." data-testid="cart-obs" />
          </div>

          <Card className="bg-blue-500/5 border-blue-500/20">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider font-bold text-blue-400 mb-1">
                Prazo estimado
              </p>
              <p className="text-sm text-white">
                O prazo final será calculado pelo sistema com base no produto de maior prazo do pedido.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <span className="text-zinc-400">Total ({cart.reduce((s, i) => s + i.quantidade, 0)} itens)</span>
                <span className="text-3xl font-black font-['Barlow_Condensed'] text-white" data-testid="cart-total">{fmt(total)}</span>
              </div>
              <Button onClick={finalizarPedido} disabled={creating} className="w-full bg-[var(--cor-primaria,#007AFF)] hover:opacity-90 h-12 text-lg font-bold" data-testid="finalizar-pedido-btn">
                {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Finalizar Pedido'}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
